/**
 * Expiring Clearances Alert Component
 * Shows alert banner and dialog for clearances expiring/expired
 */
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Bell, Mail, Send, Users, Info, AlertCircle, Clock, CheckSquare, Square } from "lucide-react";

export const ExpiringClearancesAlert = ({
  expiringClearances,
  alertSettings,
  setAlertSettings,
  alertDialogOpen,
  setAlertDialogOpen,
  onSendAlert,
  sendingAlert,
  onSendToStaff
}) => {
  // State for staff selection and confirmation
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [staffSelectionOpen, setStaffSelectionOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'director' or 'staff'
  
  if (expiringClearances.length === 0) return null;

  const staffWithEmails = expiringClearances.filter(s => s.email);

  // Separate expired items from expiring items
  const expiredItems = [];
  const expiringSoonItems = [];

  expiringClearances.forEach(staff => {
    staff.clearances.forEach(c => {
      const item = {
        staffName: staff.name,
        staffEmail: staff.email,
        clearanceName: c.type || c.clearance_name,
        daysUntilExpiry: c.days_until_expiry,
        expirationDate: c.expiration_date
      };
      if (c.days_until_expiry < 0) {
        expiredItems.push(item);
      } else {
        expiringSoonItems.push(item);
      }
    });
  });

  // Sort items: expired by most overdue first, expiring by soonest first
  expiredItems.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
  expiringSoonItems.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);

  const totalItems = expiredItems.length + expiringSoonItems.length;

  // Get urgency breakdown for expiring items
  const urgentCount = expiringSoonItems.filter(c => c.daysUntilExpiry <= 7).length;
  const highCount = expiringSoonItems.filter(c => c.daysUntilExpiry > 7 && c.daysUntilExpiry <= 14).length;
  const mediumCount = expiringSoonItems.filter(c => c.daysUntilExpiry > 14).length;

  return (
    <>
      <Card className={`mb-6 border-2 ${expiredItems.length > 0 ? 'bg-gradient-to-r from-red-50 to-orange-50 border-red-300' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300'}`}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${expiredItems.length > 0 ? 'bg-red-100' : 'bg-amber-100'}`}>
                {expiredItems.length > 0 ? (
                  <AlertCircle className="w-6 h-6 text-red-600" />
                ) : (
                  <Bell className="w-6 h-6 text-amber-600" />
                )}
              </div>
              <div>
                <p className={`text-lg font-semibold ${expiredItems.length > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                  {expiredItems.length > 0 ? '🚨' : '⚠️'} {totalItems} Clearance(s) Need Attention
                </p>
                <p className={`text-sm ${expiredItems.length > 0 ? 'text-red-600' : 'text-amber-600'}`}>
                  {expiringClearances.length} staff member(s) affected
                </p>
                {/* Show clear status breakdown */}
                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                  {expiredItems.length > 0 && (
                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full font-bold">
                      🔴 {expiredItems.length} EXPIRED
                    </span>
                  )}
                  {urgentCount > 0 && (
                    <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full font-medium">
                      🟠 {urgentCount} urgent (≤7 days)
                    </span>
                  )}
                  {highCount > 0 && (
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
                      🟡 {highCount} soon (≤14 days)
                    </span>
                  )}
                  {mediumCount > 0 && (
                    <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                      ⏰ {mediumCount} within {alertSettings.daysWarning} days
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {staffWithEmails.length > 0 && onSendToStaff && (
                <Button
                  onClick={() => {
                    // Initialize selection with all staff
                    setSelectedStaffIds(staffWithEmails.map(s => s.id || s.email));
                    setStaffSelectionOpen(true);
                  }}
                  disabled={sendingAlert}
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Email Staff ({staffWithEmails.length})
                </Button>
              )}
              <Button
                onClick={() => setAlertDialogOpen(true)}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Alert Email
              </Button>
            </div>
          </div>
          
          {/* EXPIRED ITEMS SECTION - Show prominently at top if any exist */}
          {expiredItems.length > 0 && (
            <div className="mt-4 pt-4 border-t border-red-200">
              <p className="text-sm font-bold text-red-800 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                EXPIRED - Immediate Action Required ({expiredItems.length})
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {expiredItems.slice(0, 6).map((item, idx) => (
                  <div key={idx} className="bg-red-100 rounded-lg p-3 text-sm border border-red-300">
                    <p className="font-semibold text-red-900">{item.staffName}</p>
                    <p className="text-red-800 text-xs mt-1">{item.clearanceName}</p>
                    <p className="text-red-700 font-bold text-xs mt-1">
                      🔴 Expired {Math.abs(item.daysUntilExpiry)} day{Math.abs(item.daysUntilExpiry) !== 1 ? 's' : ''} ago
                    </p>
                  </div>
                ))}
                {expiredItems.length > 6 && (
                  <div className="bg-red-100 rounded-lg p-3 text-sm flex items-center justify-center text-red-700 border border-red-300">
                    +{expiredItems.length - 6} more expired
                  </div>
                )}
              </div>
            </div>
          )}

          {/* EXPIRING SOON SECTION */}
          {expiringSoonItems.length > 0 && (
            <div className={`mt-4 pt-4 ${expiredItems.length > 0 ? 'border-t border-amber-200' : 'border-t border-amber-200'}`}>
              <p className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Expiring Soon - Within {alertSettings.daysWarning} Days ({expiringSoonItems.length})
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {expiringSoonItems.slice(0, 6).map((item, idx) => {
                  const isUrgent = item.daysUntilExpiry <= 7;
                  const isHigh = item.daysUntilExpiry > 7 && item.daysUntilExpiry <= 14;
                  return (
                    <div key={idx} className={`rounded-lg p-3 text-sm border ${isUrgent ? 'bg-orange-100 border-orange-300' : isHigh ? 'bg-yellow-100 border-yellow-300' : 'bg-amber-50 border-amber-200'}`}>
                      <p className={`font-semibold ${isUrgent ? 'text-orange-900' : isHigh ? 'text-yellow-900' : 'text-amber-900'}`}>{item.staffName}</p>
                      <p className={`text-xs mt-1 ${isUrgent ? 'text-orange-800' : isHigh ? 'text-yellow-800' : 'text-amber-700'}`}>{item.clearanceName}</p>
                      <p className={`font-medium text-xs mt-1 ${isUrgent ? 'text-orange-700' : isHigh ? 'text-yellow-700' : 'text-amber-600'}`}>
                        {isUrgent ? '🟠' : isHigh ? '🟡' : '⏰'} {item.daysUntilExpiry} day{item.daysUntilExpiry !== 1 ? 's' : ''} left
                      </p>
                    </div>
                  );
                })}
                {expiringSoonItems.length > 6 && (
                  <div className="bg-amber-50 rounded-lg p-3 text-sm flex items-center justify-center text-amber-600 border border-amber-200">
                    +{expiringSoonItems.length - 6} more expiring
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" />
              Send Clearance Alert Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Recipient Info Box */}
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Email will be sent to:</p>
                  <p className="text-blue-600">{alertSettings.recipientEmail || 'Enter email below'}</p>
                </div>
              </div>
            </div>

            <div>
              <Label>Recipient Email (Director/Admin)</Label>
              <Input
                type="email"
                value={alertSettings.recipientEmail}
                onChange={(e) => setAlertSettings({
                  ...alertSettings,
                  recipientEmail: e.target.value
                })}
                placeholder="Enter email address"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Warning Period (Days Before Expiration)</Label>
              <Input
                type="number"
                value={alertSettings.daysWarning}
                onChange={(e) => setAlertSettings({
                  ...alertSettings,
                  daysWarning: parseInt(e.target.value) || 30
                })}
                min={1}
                max={90}
                className="mt-1"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Include Summary Report</Label>
              <Switch
                checked={alertSettings.sendSummary}
                onCheckedChange={(checked) => setAlertSettings({
                  ...alertSettings,
                  sendSummary: checked
                })}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Also Email Staff Members Directly</Label>
                <p className="text-xs text-gray-500 mt-0.5">{staffWithEmails.length} staff have emails on file</p>
              </div>
              <Switch
                checked={alertSettings.sendIndividual}
                onCheckedChange={(checked) => setAlertSettings({
                  ...alertSettings,
                  sendIndividual: checked
                })}
              />
            </div>

            {/* Detailed Preview - Separated by status */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm font-semibold text-gray-800 mb-2">
                📧 Email Preview - What's Being Sent:
              </p>
              <p className="text-sm text-gray-700 mb-2">
                <strong>{totalItems} clearance(s)</strong> for <strong>{expiringClearances.length} staff member(s)</strong>
              </p>
              
              <div className="bg-white rounded p-2 max-h-48 overflow-y-auto space-y-3">
                {/* Expired Items */}
                {expiredItems.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-red-700 mb-1">🔴 EXPIRED ({expiredItems.length}):</p>
                    <ul className="text-xs text-red-600 space-y-0.5 ml-2">
                      {expiredItems.map((item, idx) => (
                        <li key={idx}>
                          <span className="font-medium">{item.staffName}</span> - {item.clearanceName} 
                          <span className="text-red-800 font-bold"> ({Math.abs(item.daysUntilExpiry)}d overdue)</span>
                          {alertSettings.sendIndividual && item.staffEmail && (
                            <span className="text-green-600 ml-1">✓</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Expiring Soon Items */}
                {expiringSoonItems.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-amber-700 mb-1">⏰ EXPIRING SOON ({expiringSoonItems.length}):</p>
                    <ul className="text-xs text-amber-600 space-y-0.5 ml-2">
                      {expiringSoonItems.map((item, idx) => (
                        <li key={idx}>
                          {item.daysUntilExpiry <= 7 ? '🟠' : item.daysUntilExpiry <= 14 ? '🟡' : '⏰'} 
                          <span className="font-medium">{item.staffName}</span> - {item.clearanceName} 
                          <span className="font-medium"> ({item.daysUntilExpiry}d left)</span>
                          {alertSettings.sendIndividual && item.staffEmail && (
                            <span className="text-green-600 ml-1">✓</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={() => {
                setConfirmAction('director');
                setConfirmDialogOpen(true);
              }}
              disabled={sendingAlert || !alertSettings.recipientEmail}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
            >
              {sendingAlert ? (
                'Sending...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to {alertSettings.recipientEmail?.split('@')[0] || 'Director'}
                </>
              )}
            </Button>
            <Button
              onClick={() => setAlertDialogOpen(false)}
              variant="outline"
              className="flex-1 border-amber-300 text-amber-700"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Staff Selection Dialog */}
      <Dialog open={staffSelectionOpen} onOpenChange={setStaffSelectionOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" />
              Select Staff to Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                Select which staff members should receive clearance reminder emails directly.
              </p>
            </div>
            
            {/* Select All / Deselect All */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSelectedStaffIds(staffWithEmails.map(s => s.id || s.email))}
                className="text-xs"
              >
                <CheckSquare className="w-3 h-3 mr-1" />
                Select All
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSelectedStaffIds([])}
                className="text-xs"
              >
                <Square className="w-3 h-3 mr-1" />
                Deselect All
              </Button>
            </div>
            
            {/* Staff List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {staffWithEmails.map((staff) => {
                const staffId = staff.id || staff.email;
                const isSelected = selectedStaffIds.includes(staffId);
                const staffClearances = staff.clearances || [];
                
                return (
                  <div
                    key={staffId}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200 hover:border-amber-200'
                    }`}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedStaffIds(selectedStaffIds.filter(id => id !== staffId));
                      } else {
                        setSelectedStaffIds([...selectedStaffIds, staffId]);
                      }
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{staff.name}</p>
                      <p className="text-xs text-gray-500">{staff.email}</p>
                      <p className="text-xs text-amber-600 mt-1">
                        {staffClearances.length} clearance(s) need attention
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-sm text-gray-600">
              <strong>{selectedStaffIds.length}</strong> of {staffWithEmails.length} staff selected
            </div>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              onClick={() => {
                if (selectedStaffIds.length > 0) {
                  setConfirmAction('staff');
                  setConfirmDialogOpen(true);
                }
              }}
              disabled={sendingAlert || selectedStaffIds.length === 0}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              Email {selectedStaffIds.length} Staff
            </Button>
            <Button
              onClick={() => setStaffSelectionOpen(false)}
              variant="outline"
              className="flex-1 border-amber-300 text-amber-700"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" />
              Confirm Email Send
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === 'director' ? (
                <>
                  Are you sure you want to send the clearance alert email to{' '}
                  <strong>{alertSettings.recipientEmail}</strong>?
                  {alertSettings.sendIndividual && staffWithEmails.length > 0 && (
                    <>
                      <br /><br />
                      This will also email <strong>{staffWithEmails.length} staff members</strong> directly.
                    </>
                  )}
                </>
              ) : (
                <>
                  Are you sure you want to send reminder emails to{' '}
                  <strong>{selectedStaffIds.length} staff member(s)</strong>?
                  <br /><br />
                  <span className="text-amber-600">
                    Staff will receive individual emails about their expiring/expired clearances.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmAction === 'director') {
                  onSendAlert();
                } else if (confirmAction === 'staff') {
                  // Filter to only selected staff and call onSendToStaff
                  const selectedStaff = staffWithEmails.filter(s => 
                    selectedStaffIds.includes(s.id || s.email)
                  );
                  onSendToStaff(selectedStaff);
                  setStaffSelectionOpen(false);
                }
                setConfirmDialogOpen(false);
              }}
              className="bg-amber-500 hover:bg-amber-600"
            >
              Yes, Send Email{confirmAction === 'staff' && selectedStaffIds.length > 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExpiringClearancesAlert;
